import renderRadioGroup from 'components/FormControls/radioGroup'
import renderInputField from 'components/FormControls/textInput'
import { Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import * as Yup from 'yup'
import { ACCESS_CREATE } from 'modules/Services/types'
import ActionButtons from 'modules/Forms/Account/AccessCreate/subcomponents/ActionButtons'
import PageHeader from 'components/PageHeader'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const validationSchema = Yup.object().shape({
  role: Yup.string().required('common::validation_messages.required'),
  profile_url: Yup.string()
    .required('common::validation_messages.required')
    .test(
      'isVk',
      'services::forms.account_access_create.vk.fields.profile_url.validation_messages.not_vk',
      function test(value) {
        const { path, createError } = this

        if (value.length == 0) {
          return createError({
            path,
            message: 'common::validation_messages.required',
          })
        }

        const isValid = value.match(/^http(|s)?:\/\/vk\.com/)

        return (
          isValid ||
          createError({
            path,
            message:
              'services::forms.account_access_create.vk.fields.profile_url.validation_messages.not_vk',
          })
        )
      },
    ),
})

let VkAccountAccessCreateForm = ({
  handleSubmit,
  submitting,
  form,
  onClose,
  networkType,
  accountId,
  accessId,
}) => {
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: ACCESS_CREATE,
          payload: {
            networkType,
            accountId,
            data: values,
            formName: form,
          },
        })
      }).then(onClose),
    [onClose, networkType, accountId],
  )

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <PageHeader className={'margin-bottom-16'}>
        {t(`services::networks.${networkType}`)}
      </PageHeader>
      <Typography
        variant={'Body-20'}
        color={'Label-Secondary'}
        className={clsx({
          ['margin-bottom-64']: !breakpoint.md,
          ['margin-bottom-48']: breakpoint.md,
        })}
      >
        {t('services::forms.account_access_create.description')}
      </Typography>

      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="profile_url"
          component={renderInputField}
          t={t}
          label={t('services::forms.account_access_create.vk.fields.profile_url.label')}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Typography variant={'Body-20'} className={formStyles['legend']}>
          {t('services::forms.account_access_create.vk.fields.role.label')}
        </Typography>

        <Field
          name="role"
          t={t}
          component={renderRadioGroup}
          options={['manager', 'reports'].map((option) => ({
            value: option,
            label: t(`services::forms.account_access_create.vk.fields.role.options.${option}`),
          }))}
        />
      </div>

      <ActionButtons submitting={submitting} onClose={onClose} accessId={accessId} />
    </form>
  )
}

VkAccountAccessCreateForm = reduxForm({
  form: 'VkAccountAccessCreateForm',
  initialValues: {
    profile_url: '',
    role: 'manager',
  },
  asyncValidate: validator(validationSchema),
})(VkAccountAccessCreateForm)

VkAccountAccessCreateForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  array: PropTypes.object,
  autofill: PropTypes.func,
  form: PropTypes.string,
  onClose: PropTypes.func,
  networkType: PropTypes.string,
  accountId: PropTypes.string,
  accessId: PropTypes.string,
}

export default VkAccountAccessCreateForm
